function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import useRouter from 'found/lib/useRouter';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import BaseForm from 'react-formal';
import { FormattedMessage } from 'react-intl';
import Button from './Button';
import { useDialog } from './DialogContext';
import FieldGroup from './FieldGroup';
import FieldSet from './FieldSet';
import FormField from './FormField';
import FormGroup from './FormGroup';
import FormLabel from './FormLabel';
import FormMessage from './FormMessage'; // eslint-disable-next-line import/no-cycle

import HorizontalFieldGroup from './HorizontalFieldGroup';
import { useToast } from './ToastContext';
const FormSubmit = React.forwardRef(({
  as: Component = Button,
  triggers,
  type = triggers ? 'button' : 'submit',
  disabled,
  finished,
  children,
  ...outerProps
}, ref) => React.createElement(BaseForm.Submit, _extends({
  triggers: triggers
}, outerProps), ({
  submitting,
  props
}) => React.createElement(Component, _extends({
  ref: ref,
  type: type,
  disabled: disabled || finished || submitting
}, props, {
  busy: submitting
}), children)));
FormSubmit.displayName = 'FormSubmit';
export function useShowDiscardChangesDialog() {
  const dialog = useDialog();
  return () => dialog.open(React.createElement(FormattedMessage, {
    id: "@bfly/ui:form.confirmDiscardChanges.content",
    defaultMessage: "Are you sure you want to leave without saving? Your changes will be lost."
  }), {
    title: React.createElement(FormattedMessage, {
      id: "@bfly/ui:form.confirmDiscardChanges.title",
      defaultMessage: "Discard Unsaved Changes?"
    })
  }).then(Boolean);
}
const propTypes = {
  /** A yup schema, required for forms that need validation */
  schema: PropTypes.object,
  // Hack to match TS props.

  /** The controlled form value, use `defaultValue` for uncontrolled forms */
  value: PropTypes.object,

  /**
   * Callback fired with the next, _unvalidated_, form value in response to an
   * input change
   */
  onChange: PropTypes.func,

  /**
   * A Promise returning function passed the _validated_ form value that runs
   * submit logic, such as posting to an API. While the promise resolves,
   * Form.Submit buttons will be disabled and show a spinner
   */
  submitForm: PropTypes.func,

  /**
   * An optional toast message to fire on submit success, requires
   * NavigationProvider be rendered as an ancestor
   */
  successMessage: PropTypes.node,

  /**
   * An optional toast message to fire on submit failure (when submitForm
   * rejects), requires NavigationProvider be rendered as an ancestor
   */
  failureMessage: PropTypes.node,

  /**
   * Prevent navigation when the form is "dirty", as determined by changes
   * existing without a successful submit event.
   * Pass a function to further limit when to warn. The callback
   * accepts the next location and if it returns false it does not warn.
   */
  warnOnUnsavedNavigation: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
};
/**
 * A light wrapper around [react-formal](https://github.com/jquense/react-formal)
 * with some application specific hooks for preventing navigation on dirty form
 * values via Found and running Relay mutations.
 *
 * Exports as static Properties:
 *
 *   - `FieldSet: FormFieldSet`
 *   - `FieldGroup: FieldGroup`
 *   - `HorizonalFieldGroup: HorizonalFieldGroup`
 *   - `Field: FormField`
 *   - `FieldArray: FieldArray`
 *   - `Group: FormGroup`
 *   - `Label: FormLabel`
 *   - `Message: FormMessage`
 *   - `Submit: FormSubmit`
 */

const Form = React.forwardRef(({
  onChange,
  submitForm,
  successMessage,
  failureMessage,
  warnOnUnsavedNavigation,
  formContext: context,
  // React Redux consumes the "context" prop.
  ...props
}, ref) => {
  const routerState = useRouter(); // can actually be null

  const router = routerState && routerState.router;
  const toast = useToast();
  const showDiscardChangesDialog = useShowDiscardChangesDialog();
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (!router || !warnOnUnsavedNavigation) return undefined;
    const removeTransitionHook = router.addTransitionHook(async location => {
      if (!dirtyRef.current) return undefined;
      if (!location) return false;

      if (typeof warnOnUnsavedNavigation === 'function' && !warnOnUnsavedNavigation(location)) {
        return null;
      }

      const confirmed = await showDiscardChangesDialog();

      if (confirmed) {
        // FIXME: This prevents the confirmation from showing up again if the
        //  navigation action triggers a redirect in the router, as that
        //  redirect will happen before this component unmounts.
        removeTransitionHook();
      }

      return confirmed;
    });
    return removeTransitionHook;
  }, [warnOnUnsavedNavigation, router, showDiscardChangesDialog]);
  const handleChange = useCallback((item, changedPaths) => {
    dirtyRef.current = true;
    if (onChange) onChange(item, changedPaths);
  }, [onChange]);
  const handleSuccess = useCallback(() => {
    if (toast && successMessage) toast.success(successMessage);
  }, [successMessage, toast]);
  const handleFailure = useCallback(err => {
    if (toast && failureMessage) toast.error(failureMessage);
    throw err;
  }, [failureMessage, toast]);
  const runSubmit = useCallback(formValue => {
    dirtyRef.current = false;
    return Promise.resolve(submitForm(formValue)).then(handleSuccess, handleFailure);
  }, [handleFailure, handleSuccess, submitForm]);
  return React.createElement(BaseForm, _extends({}, props, {
    ref: ref,
    debug: process.env.NODE_ENV !== "production",
    context: context,
    submitForm: submitForm && runSubmit,
    onChange: handleChange
  }));
});
Form.displayName = 'Form';
Form.propTypes = propTypes; // TODO: The generic isn't accessible for explicit use b/c of forwardRef.

export default Object.assign(Form, {
  FieldSet,
  FieldGroup,
  HorizontalFieldGroup,
  Field: FormField,
  FieldArray: BaseForm.FieldArray,
  Group: FormGroup,
  Label: FormLabel,
  Message: FormMessage,
  Submit: FormSubmit,
  // FIXME: This should just use hoist-non-react-statics, but I can't get the
  //  typing to work right.
  getter: BaseForm.getter
});